import { Tag } from "primereact/tag";
import { Fragment, ReactNode } from "react";
import {
  IRemitCluster,
  IRemitClusterLine,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import { PverifyRequestLink } from "./RemitAccuracyLinks";
import {
  formatConfidence,
  formatRemitValue,
  lineLabel,
} from "./remitAccuracyWording";

type IExample = NonNullable<IRemitCluster["example"]>;

type EngineTag = "READ" | "VALUE" | null;

const ENGINE_TAG_LABELS: Record<"READ" | "VALUE", string> = {
  READ: "Engine read this",
  VALUE: "Engine value",
};

function Tags({
  engineTag,
  equalsRemit,
  aiTag,
}: {
  engineTag: EngineTag;
  equalsRemit: boolean;
  /** CLE-2820 — "AI · 0.87" on the place the AI read, when the AI comparison is on. */
  aiTag?: string | null;
}) {
  return (
    <div className="flex gap-2">
      {engineTag && (
        <Tag severity="danger" value={ENGINE_TAG_LABELS[engineTag]} />
      )}
      {aiTag && (
        <Tag
          severity="info"
          value={aiTag}
          data-testid="remitAccuracy.cluster.aiTag"
        />
      )}
      {equalsRemit && <Tag severity="success" value="= Remit" />}
    </div>
  );
}

function Row({
  testId,
  location,
  message,
  network,
  value,
  engineTag,
  equalsRemit,
  aiTag,
}: {
  testId: string;
  location: string;
  message: ReactNode;
  network: string;
  value: ReactNode;
  engineTag: EngineTag;
  equalsRemit: boolean;
  aiTag?: string | null;
}) {
  return (
    <tr
      data-testid={testId}
      className={equalsRemit ? "bg-green-50" : undefined}
    >
      <td className="p-2 white-space-nowrap font-mono text-sm">{location}</td>
      <td className="p-2">{message}</td>
      <td className="p-2">{network}</td>
      <td className="p-2">{value}</td>
      <td className="p-2">
        <Tags engineTag={engineTag} equalsRemit={equalsRemit} aiTag={aiTag} />
      </td>
    </tr>
  );
}

/**
 * CLE-2818 — the example check's stored response, located so it can be compared against the raw pVerify JSON:
 * the summary field the engine read (on a summary read), then the lines grouped by service line, each with its
 * `ServiceDetails[i]` / `EligibilityDetails[j]` position. The engine tag goes on the summary or on the one line
 * the engine read, never on a line merely because its value is the same.
 */
export function RemitAccuracyExampleResponse({
  example,
  pattern,
  benefit,
}: {
  example: IExample;
  pattern: IRemitCluster["remitLinePattern"];
  benefit: RemitScoredBenefit;
}) {
  const valueHeader =
    benefit === "COINSURANCE_PERCENT" ? "Co-insurance" : "Copay";
  const valueCell = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">no value</span>
    ) : (
      formatRemitValue(amount, benefit)
    );
  const request = <PverifyRequestLink requestId={example.pverifyRequestId} />;
  const linesOf = (serviceIndex: number) =>
    example.lines.filter((line) => line.serviceIndex === serviceIndex);
  const aiTagText = example.ai
    ? `AI · ${formatConfidence(example.ai.confidence)}`
    : null;
  const aiLocation = (ai: NonNullable<IExample["ai"]>) => {
    if (!ai.location) return "nothing (none)";
    return ai.location.type === "SUMMARY"
      ? `${ai.location.block} › ${ai.location.field}`
      : `ServiceDetails[${ai.location.serviceIndex}] › EligibilityDetails[${ai.location.lineIndex}]`;
  };

  return (
    <>
      <p className="mt-0" data-testid="remitAccuracy.cluster.exampleIntro">
        {pattern ? (
          <>
            The remit equals the <strong>{pattern.line}</strong> line on{" "}
            {pattern.checks} of {pattern.examined} eligibility checks examined,
            example from Pverify Request {request} is below:
          </>
        ) : (
          <>Example from Pverify Request {request} is below:</>
        )}
      </p>
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse"
          data-testid="remitAccuracy.cluster.lines"
        >
          <thead>
            <tr className="text-left border-bottom-1 surface-border">
              <th className="p-2">Where in the response</th>
              <th className="p-2">Line message</th>
              <th className="p-2">Network</th>
              <th className="p-2">{valueHeader}</th>
              <th className="p-2" aria-label="Tags" />
            </tr>
          </thead>
          <tbody>
            {example.summary && (
              <Row
                testId="remitAccuracy.cluster.summaryRow"
                location={`${example.summary.block} › ${example.summary.field}`}
                message={
                  example.summary.notes ?? (
                    <span className="text-color-secondary">no notes</span>
                  )
                }
                network="In"
                value={valueCell(example.summary.value)}
                engineTag="READ"
                equalsRemit={example.summary.equalsRemit}
                aiTag={example.summary.aiTag ? aiTagText : null}
              />
            )}
            {example.serviceLines.map((service) => (
              <Fragment key={service.serviceIndex}>
                <tr
                  className="surface-100"
                  data-testid={`remitAccuracy.cluster.serviceLine.${service.serviceIndex}`}
                >
                  <td className="p-2 font-semibold" colSpan={5}>
                    {`${service.serviceName} (ServiceDetails[${service.serviceIndex}])`}
                  </td>
                </tr>
                {linesOf(service.serviceIndex).map(
                  (line: IRemitClusterLine) => (
                    <Row
                      key={line.lineIndex}
                      testId={`remitAccuracy.cluster.line.${line.serviceIndex}.${line.lineIndex}`}
                      location={`EligibilityDetails[${line.lineIndex}]`}
                      message={lineLabel(line)}
                      network={line.planNetworkIndicator ?? "—"}
                      value={valueCell(line.value)}
                      engineTag={line.engineTag}
                      equalsRemit={line.equalsRemit}
                      aiTag={line.aiTag ? aiTagText : null}
                    />
                  )
                )}
              </Fragment>
            ))}
            {!example.summary && example.lines.length === 0 && (
              <tr>
                <td className="p-2" colSpan={5}>
                  No in-network lines on the service line the engine read.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {example.ai && !example.ai.shown && (
        <p
          className="text-sm mt-2 mb-0"
          data-testid="remitAccuracy.cluster.aiNotShown"
        >
          The AI answered {formatRemitValue(example.ai.value, benefit)} (
          {formatConfidence(example.ai.confidence)}), read from{" "}
          {aiLocation(example.ai)}, which is not among the rows above.
        </p>
      )}
      {example.engineValueLines > 1 && (
        <p
          className="text-sm mt-2 mb-0"
          data-testid="remitAccuracy.cluster.engineValueNote"
        >
          The engine doesn&apos;t record which of these{" "}
          {example.engineValueLines} lines it read.
        </p>
      )}
      {example.hiddenRemitServiceLines > 0 && (
        <p
          className="text-sm mt-2 mb-0"
          data-testid="remitAccuracy.cluster.hiddenServiceLinesNote"
        >
          {example.remitValue === 0
            ? `Also ${formatRemitValue(0, benefit)} on ${
                example.hiddenRemitServiceLines
              } other service line${
                example.hiddenRemitServiceLines === 1 ? "" : "s"
              }.`
            : `+${example.hiddenRemitServiceLines} more service line${
                example.hiddenRemitServiceLines === 1 ? " carries" : "s carry"
              } this value.`}
        </p>
      )}
      <p className="text-xs text-color-secondary mb-0">
        Positions are in the stored response, before this account&apos;s payer
        filters.
        {example.outOfNetworkLines > 0 &&
          ` ${example.outOfNetworkLines} out-of-network lines not shown.`}
      </p>
    </>
  );
}
